import React from 'react';
import {Icon, Link, Typography} from '@bonprix/design-system';
import {SearchTagList} from './SearchTagList.jsx';
import {SearchTrinityEvents} from '../../helper/search-tracking.js';
import {capitalizeWords} from '../../helper/text-case.js';
import {FOCUS_VISIBLE_RING_CLASSES} from '../hooks/use-keyboard-focus.js';

const MAX_RECENT = 10;

const CHIP_CLASS =
    'flex items-center gap-2 min-h-8 max-w-full px-3 py-1 rounded border border-solid border-clr-on-bg-high ' +
    'has-[[data-chip-term]:focus-visible]:outline has-[[data-chip-term]:focus-visible]:outline-2 ' +
    'has-[[data-chip-term]:focus-visible]:outline-offset-2 has-[[data-chip-term]:focus-visible]:outline-clr-on-bg-high';

const TERM_CLASS =
    'border-0 bg-transparent p-0 cursor-pointer text-left text-sm text-clr-on-bg-high focus-visible:outline-none ' +
    '[@media(hover:hover)]:hover:underline';

const INSERT_CLASS =
    'flex items-center shrink-0 border-0 bg-transparent p-0 cursor-pointer [&_svg]:w-3.5 [&_svg]:h-3.5 ' +
    FOCUS_VISIBLE_RING_CLASSES;

/**
 * Recent ("last") searches shown in the empty search overlay as bordered "search tags".
 * - Clicking a tag's term triggers the search immediately.
 * - Clicking the search-form-arrow inserts the term into the field and activates it (no search).
 * Layout (2-row collapse + "mehr/weniger anzeigen" toggle, max 10) is shared via {@link SearchTagList}.
 */
export const RecentSearches = ({
    recentSearches,
    historyHeader,
    clearLabel,
    showMoreLabel,
    showLessLabel,
    insertLabel,
    onSelectTerm,
    onInsertTerm,
    clearSearchHistory,
}) => {
    const terms = (recentSearches || []).slice(0, MAX_RECENT);
    const insertAriaLabel = (term) =>
        (insertLabel || '„{term}“ in das Suchfeld übernehmen').replace('{term}', capitalizeWords(term));

    const handleTermClick = (term, index) => {
        SearchTrinityEvents.sendSearchRecentSearchesAction('clickRecentSearchTerm', term, index);
        onSelectTerm(term);
    };

    const handleCopyTerm = (term, index) => {
        SearchTrinityEvents.sendSearchRecentSearchesAction('copyRecentSearchTerm', term, index);
        onInsertTerm(term);
    };

    const handleDeleteAll = () => {
        SearchTrinityEvents.sendSearchRecentSearchesAction('deleteAllRecentSearches');
        clearSearchHistory();
    };

    const handleToggleExpanded = (expanded) => {
        SearchTrinityEvents.sendSearchRecentSearchesAction(
            expanded ? 'showMoreRecentSearches' : 'showLessRecentSearches',
        );
    };

    return (
        <SearchTagList
            terms={terms}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            onToggleExpanded={handleToggleExpanded}
            header={
                <div className="flex items-baseline justify-start gap-3 mb-2">
                    <Typography variant="h6" component="span" className="text-clr-on-bg-mid">
                        {historyHeader || 'Deine letzten Suchen'}
                    </Typography>
                    <Link small component="button" onClick={handleDeleteAll}>
                        {clearLabel || 'löschen'}
                    </Link>
                </div>
            }
            renderChip={(term, isHidden, index) => (
                <div className={CHIP_CLASS}>
                    <button
                        type="button"
                        className={TERM_CLASS}
                        data-chip-term
                        tabIndex={isHidden ? -1 : 0}
                        onClick={() => handleTermClick(term, index)}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        {capitalizeWords(term)}
                    </button>
                    <button
                        type="button"
                        className={INSERT_CLASS}
                        tabIndex={isHidden ? -1 : 0}
                        aria-label={insertAriaLabel(term)}
                        onClick={() => handleCopyTerm(term, index)}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Icon variant="search-form-arrow" />
                    </button>
                </div>
            )}
        />
    );
};
